import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { LOGO_URL } from "@/lib/data";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 border-b border-black/5 bg-white/70 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center">
          <img
            src={LOGO_URL}
            alt="Brand Mitra 24"
            className="h-11 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative font-manrope text-sm font-semibold tracking-wide transition-colors duration-300 ${
                  isActive ? "text-bm-blue" : "text-bm-ink hover:text-bm-blue"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            data-testid="nav-cta-button"
            onClick={() => navigate("/contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-bm-blue px-6 py-3 font-manrope text-sm font-bold text-white transition-transform duration-300 hover:-translate-y-0.5 hover:bg-bm-navy"
          >
            Start a Project
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="md:hidden text-bm-ink"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-black/5 bg-white/95 backdrop-blur-xl"
          >
            <nav className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-syne text-2xl font-bold text-bm-ink"
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                data-testid="nav-mobile-cta-button"
                onClick={() => {
                  setOpen(false);
                  navigate("/contact");
                }}
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-bm-blue px-6 py-3 font-manrope text-sm font-bold text-white"
              >
                Start a Project <ArrowUpRight className="h-4 w-4" />
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
